import { StoreonModule } from 'storeon';
import { IEvents, IState } from 'src/store';

export type FenceSide = 'front' | 'back' ; // Спереди | Сзади
export type FenceType = 'picket' | 'pailing' ; // Штакетник | Профнастил

interface IFenceStoreModule {
  numberOfImage: number;
  fenceSide: FenceSide;
  fenceType: FenceType;
  fenceColor: string;
  dzValue: number | null; // ДЗ - длина забора
  vzValue: string; // ВЗ - высота забора
  tpValue: string; // ТП - тип периметра
  rsValue: number | null; // РС - расстояние между столбами
  ssValue: string; // сечение столба
  pCalc: number | null; // П - количество листов профнастила
  sCalc: number | null; // С - количество столбов
  dsCalc: number | null; // ДС - длина столбов
  mCalc: number | null; // М - количество саморезов
  nCalc: number | null; // Н - длина направляющих
  kvValue: string; // КВ - количество ворот
  shvValue: string; // ШВ - ширина ворот
  kkValue: string; // КК - количество калиток
  shkValue: string; // ШК - ширина калитки
  kpCalc: number | null; // КП - количество пролетов
  kppCalc: number | null; // КПП - количество П-планок
  pgvValue: string; // ПГВ - параметр горизонталей вид
  pgkValue: string; // ПГК - параметр горизонталей количество
  kValue: string; // крепления
  pailingCoating: string; // Параметры профнастила - Тип покрытия
  pailingType: string; // Параметры профнастила - Вид профнастила
  pailingPBar: string; // Параметры профнастила - П-образная планка
  picketCoating: string; // Параметры штакетника - Тип покрытия
  picketType: string; // Параметры штакетника - Вид штакетника
  modalIsOpen: boolean;
  totalPrice: number; // Расчетная стоимость
}

export interface IFenceState {
  fenceStore: IFenceStoreModule;
}

export interface IFenceEvents {
  'fence/set-numberOfImage': { numberOfImage: number };
  'fence/set-fence-side': { fenceSide: FenceSide };
  'fence/set-fence-type': { fenceType: FenceType };
  'fence/set-fence-color': { fenceColor: string };
  'fence/set-dz-value': { dzValue: number | null };
  'fence/set-vz-value': { vzValue: string };
  'fence/set-tp-value': { tpValue: string };
  'fence/set-rs-value': { rsValue: number | null };
  'fence/set-ss-value': { ssValue: string };
  'fence/set-p-calc': { pCalc: number | null };
  'fence/set-s-calc': { sCalc: number | null };
  'fence/set-ds-calc': { dsCalc: number | null };
  'fence/set-m-calc': { mCalc: number | null };
  'fence/set-n-calc': { nCalc: number | null };
  'fence/set-kv-value': { kvValue: string };
  'fence/set-shv-value': { shvValue: string };
  'fence/set-kk-value': { kkValue: string };
  'fence/set-shk-value': { shkValue: string };
  'fence/set-kp-calc': { kpCalc: number | null };
  'fence/set-kpp-calc': { kppCalc: number | null };
  'fence/set-pgv-value': { pgvValue: string };
  'fence/set-pgk-value': { pgkValue: string };
  'fence/set-k-value': { kValue: string };
  'fence/set-pailing-coating': { pailingCoating: string };
  'fence/set-pailing-type': { pailingType: string };
  'fence/set-pailing-pbar': { pailingPBar: string };
  'fence/set-picket-coating': { picketCoating: string };
  'fence/set-picket-type': { picketType: string };
  'fence/set-modal-is-open': { modalIsOpen: boolean };
  'fence/set-total-price': { totalPrice: number };
}

const initialState: IFenceStoreModule = {
  numberOfImage: 1,
  fenceSide: 'front',
  fenceType: 'pailing',
  fenceColor: 'RAL 1014',
  dzValue: 15, // ДЗ - длина забора
  vzValue: '1.5', // ВЗ - высота забора
  tpValue: 'Замкнутый', // ТП - тип периметра
  rsValue: 2, // РС - расстояние между столбами
  ssValue: '50*50*2', // sp - сечение столба
  pCalc: null, // П - количество листов профнастила
  sCalc: null, // С - количество столбов
  dsCalc: null, // ДС - длина столбов
  mCalc: null, // М - количество саморезов
  nCalc: null, // Н - длина направляющих
  kvValue: '1', // КВ - количество ворот
  shvValue: '3.5', // ШВ - ширина ворот
  kkValue: '1', // КК - количество калиток
  shkValue: '0.9', // ШК - ширина калитки
  kpCalc: null, // КП - количество пролетов
  kppCalc: null, // КПП - количество П-планок
  pgvValue: '40*20*1,5', // ПГВ - параметр горизонталей вид
  pgkValue: '2', // ПГК - параметр горизонталей количество
  kValue: 'Саморез 4,8х19', // крепления (саморезы)
  pailingCoating: 'Полиэстер 0,4 мм', // Параметры профнастила - Тип покрытия
  pailingType: 'С08', // Параметры профнастила - Вид профнастила
  pailingPBar: 'Нужна', // Параметры профнастила - П-образная планка
  picketCoating: 'Полиэстер 0,4 мм', // Параметры штакетника - Тип покрытия
  picketType: 'М-образный', // Параметры штакетника - Вид штакетника
  modalIsOpen: false,
  totalPrice: 0,
}

export const fenceStoreModule: StoreonModule<IState, IEvents> = (store) => {
  store.on('@init', () => ({
    fenceStore: { ...initialState },
  }));
  store.on('fence/set-numberOfImage', (state, payload) => ({
    fenceStore: { ...state.fenceStore, numberOfImage: payload.numberOfImage },
  }));
  store.on('fence/set-fence-side', (state, payload) => ({
    fenceStore: { ...state.fenceStore, fenceSide: payload.fenceSide },
  }));
  store.on('fence/set-fence-type', (state, payload) => ({
    fenceStore: { ...state.fenceStore, fenceType: payload.fenceType },
  }));
  store.on('fence/set-fence-color', (state, payload) => ({
    fenceStore: { ...state.fenceStore, fenceColor: payload.fenceColor },
  }));
  store.on('fence/set-dz-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, dzValue: payload.dzValue },
  }));
  store.on('fence/set-vz-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, vzValue: payload.vzValue },
  }));
  store.on('fence/set-tp-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, tpValue: payload.tpValue },
  }));
  store.on('fence/set-rs-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, rsValue: payload.rsValue },
  }));
  store.on('fence/set-ss-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, ssValue: payload.ssValue },
  }));
  store.on('fence/set-p-calc', (state, payload) => ({
    fenceStore: { ...state.fenceStore, pCalc: payload.pCalc },
  }));
  store.on('fence/set-s-calc', (state, payload) => ({
    fenceStore: { ...state.fenceStore, sCalc: payload.sCalc },
  }));
  store.on('fence/set-ds-calc', (state, payload) => ({
    fenceStore: { ...state.fenceStore, dsCalc: payload.dsCalc },
  }));
  store.on('fence/set-m-calc', (state, payload) => ({
    fenceStore: { ...state.fenceStore, mCalc: payload.mCalc },
  }));
  store.on('fence/set-n-calc', (state, payload) => ({
    fenceStore: { ...state.fenceStore, nCalc: payload.nCalc },
  }));
  store.on('fence/set-kv-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, kvValue: payload.kvValue },
  }));
  store.on('fence/set-shv-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, shvValue: payload.shvValue },
  }));
  store.on('fence/set-kk-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, kkValue: payload.kkValue },
  }));
  store.on('fence/set-shk-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, shkValue: payload.shkValue },
  }));
  store.on('fence/set-kp-calc', (state, payload) => ({
    fenceStore: { ...state.fenceStore, kpCalc: payload.kpCalc },
  }));
  store.on('fence/set-kpp-calc', (state, payload) => ({
    fenceStore: { ...state.fenceStore, kppCalc: payload.kppCalc },
  }));
  store.on('fence/set-pgv-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, pgvValue: payload.pgvValue },
  }));
  store.on('fence/set-pgk-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, pgkValue: payload.pgkValue },
  }));
  store.on('fence/set-k-value', (state, payload) => ({
    fenceStore: { ...state.fenceStore, kValue: payload.kValue },
  }));
  store.on('fence/set-pailing-coating', (state, payload) => ({
    fenceStore: { ...state.fenceStore, pailingCoating: payload.pailingCoating },
  }));
  store.on('fence/set-pailing-type', (state, payload) => ({
    fenceStore: { ...state.fenceStore, pailingType: payload.pailingType },
  }));
  store.on('fence/set-pailing-pbar', (state, payload) => ({
    fenceStore: { ...state.fenceStore, pailingPBar: payload.pailingPBar },
  }));
  store.on('fence/set-picket-coating', (state, payload) => ({
    fenceStore: { ...state.fenceStore, picketCoating: payload.picketCoating },
  }));
  store.on('fence/set-picket-type', (state, payload) => ({
    fenceStore: { ...state.fenceStore, picketType: payload.picketType },
  }));
  store.on('fence/set-modal-is-open', (state, payload) => ({
    fenceStore: { ...state.fenceStore, modalIsOpen: payload.modalIsOpen },
  }));
  store.on('fence/set-total-price', (state, payload) => ({
    fenceStore: { ...state.fenceStore, totalPrice: payload.totalPrice },
  }));
};
